import { MRT_RowData } from 'material-react-table'
import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { getSelectOptionLabel } from '@/components'
import { resolveSelectOptions } from '@/helpers'
import { CustomMenuList } from '@/lib/components/Select/CustomSelectComponents'
import { Select } from '@/lib/components/Select/Select'
import { Tooltip } from '@/lib/components/Tooltip/Tooltip'
import { SelectTooltipContainerProps } from '@/lib/components/Tooltip/types'
import {
	createTooltipHandlers,
	releaseActiveTooltip,
} from '@/lib/components/Tooltip/utils'
import { FormValue } from '@/types'

import { MrtCellProps, SelectOption } from '../types'

const MIN_TOOLTIP_LENGTH = 13

const SelectTooltipContainer = memo(
	({ title, isMenuOpen, children }: SelectTooltipContainerProps) => {
		const showTooltip = (title?.length || 0) > MIN_TOOLTIP_LENGTH
		const tooltipTitle = showTooltip ? title : undefined
		const [tooltipState, setTooltipState] = useState({
			show: false,
			position: { top: 0, left: 0 },
		})
		const hideRef = useRef<() => void>(() => undefined)
		const stableHide = useRef(() => {
			hideRef.current()
		}).current

		hideRef.current = () => {
			releaseActiveTooltip(stableHide)
			setTooltipState((prev) =>
				prev.show ? { ...prev, show: false } : prev,
			)
		}

		useEffect(() => {
			return () => {
				stableHide()
			}
		}, [stableHide])

		useEffect(() => {
			if (isMenuOpen) {
				stableHide()
			}
		}, [isMenuOpen, stableHide])

		const { handleMouseEnter, handleMouseLeave } = createTooltipHandlers(
			tooltipTitle,
			{
				showTooltip: tooltipState.show,
				tooltipPosition: tooltipState.position,
				isMenuOpen,
			},
			{
				setTooltipPosition: (pos) =>
					setTooltipState((prev) => ({ ...prev, position: pos })),
				setShowTooltip: (show) =>
					setTooltipState((prev) => ({ ...prev, show })),
			},
			stableHide,
		)

		return (
			<div className="flex items-center h-full w-full">
				<div
					className="w-full"
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
				>
					{children}
				</div>
				{createPortal(
					<Tooltip
						showTooltip={tooltipState.show}
						title={tooltipTitle}
						isMenuOpen={isMenuOpen}
						tooltipPosition={tooltipState.position}
					/>,
					document.body,
				)}
			</div>
		)
	},
)

export const SelectCell = <TData extends MRT_RowData>({
	cell,
	column,
	components: customComponents,
	row,
	updateCellValue,
	isLoading,
	isMulti = false,
	title,
	isDisabled,
	isAsync = false,
	customValue,
	loadOptions,
	formatOptionLabel,
}: MrtCellProps<TData> & {
	formatOptionLabel?: (option: SelectOption) => React.ReactNode,
	isAsync?: boolean,
	loadOptions?: (inputValue: string) => Promise<SelectOption[]>
}) => {
	const {
		optionsSource,
		optionValue = 'value',
		optionLabel = 'label',
		isNumeric,
	} = cell?.column.columnDef?.meta || {}

	const options = resolveSelectOptions<SelectOption>(optionsSource, row)

	const [isMenuOpen, setIsMenuOpen] = useState(false)

	const onChange = useCallback(
		(value: FormValue) => updateCellValue?.(row?.index, column?.id, value),
		[updateCellValue, row?.index, column?.id],
	)

	const handleMenuOpen = useCallback(() => {
		setIsMenuOpen(true)
	}, [])

	const handleMenuClose = useCallback(() => {
		setIsMenuOpen(false)
	}, [])

	const getOptionLabel = useCallback(
		(option: SelectOption) => getSelectOptionLabel(option, optionLabel),
		[optionLabel],
	)

	if (isDisabled) {
		const cellValue = customValue ?? cell?.getValue()
		
		// Handle multi-select values (arrays)
		if (isMulti && Array.isArray(cellValue)) {
			const selectedOptions = cellValue
				.map(val => options?.find(opt => opt[optionValue] === val || opt === val))
				.filter(Boolean)
			
			const displayNames = selectedOptions.map(opt => opt?.[optionLabel] || opt).join(', ')
			const showTooltip = displayNames.length > MIN_TOOLTIP_LENGTH
			
			return (
				<SelectTooltipContainer
					title={showTooltip ? displayNames : undefined}
					isMenuOpen={false}
				>
					<span className="px-2 truncate block">{displayNames || '-'}</span>
				</SelectTooltipContainer>
			)
		}
		
		const selectedOption = options?.find(
			(opt) => opt[optionValue] === cellValue || opt === cellValue,
		)

		let displayContent: React.ReactNode = null
		let tooltipValue = title || ''

		if (selectedOption) {
			const name = selectedOption[optionLabel]

			const code = (selectedOption as Record<string, unknown>).code as string
			const fullPath = (selectedOption as Record<string, unknown>)
				.fullPath as string

			if (fullPath) {
				const trimmedFullPath = fullPath.includes('>')
					? fullPath.substring(0, fullPath.lastIndexOf('>'))
					: fullPath

				displayContent = (
					<span className="truncate">
						{name}
						<span className="text-xs opacity-70 ml-1">
							({trimmedFullPath})
						</span>
					</span>
				)
				tooltipValue = fullPath + ` (${code})`
			} else {
				displayContent = String(name || '')
				tooltipValue = String(name || '')
			}
		} else if (title) {
			displayContent = title
			tooltipValue = title
		} else {
			displayContent = String(cellValue || '')
			tooltipValue = String(cellValue || '')
		}

		const showTooltip = tooltipValue.length > MIN_TOOLTIP_LENGTH

		return (
			<SelectTooltipContainer
				title={showTooltip ? tooltipValue : undefined}
				isMenuOpen={false}
			>
				<span className="px-2 truncate block">{displayContent}</span>
			</SelectTooltipContainer>
		)
	}

	const commonProps = {
		value: customValue ?? cell?.getValue(),
		options,
		isDisabled,
		isLoading,
		isMulti,
		onMenuOpen: handleMenuOpen,
		onMenuClose: handleMenuClose,
		components: {
			...customComponents,
			MenuList: CustomMenuList,
		},
	}

	return (
		<SelectTooltipContainer title={title} isMenuOpen={isMenuOpen}>
			<Select
				isAsync={isAsync}
				commonProps={commonProps}
				onChange={onChange}
				optionValue={optionValue}
				optionLabel={optionLabel}
				isNumeric={isNumeric}
				columnHeader={column?.header}
				getOptionLabel={getOptionLabel}
				formatOptionLabel={formatOptionLabel}
				loadOptions={loadOptions}
				testId={cell.id}
				isTableCell
			/>
		</SelectTooltipContainer>
	)
}
